import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { userService } from '../../services/user.service';
import { FaPlus, FaUpload, FaUserPlus, FaTrash, FaFilter, FaChevronLeft, FaChevronRight, FaUsers, FaListOl } from 'react-icons/fa';
import * as XLSX from 'xlsx';

const AdminClients = () => {
    const [clients, setClients] = useState([]);
    const [allClientsList, setAllClientsList] = useState([]); // For range selection
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
    const [showRangeAssignModal, setShowRangeAssignModal] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedClients, setSelectedClients] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    // Range assignment
    const [rangeStart, setRangeStart] = useState('');
    const [rangeEnd, setRangeEnd] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [totalClients, setTotalClients] = useState(0);

    const [filter, setFilter] = useState({
        status: '',
        clientType: '',
        assignedTo: ''
    });

    const [formData, setFormData] = useState({
        companyName: '',
        address: '',
        city: '',
        pinCode: '',
        state: '',
        contactPerson: '',
        phone: '',
        email: '',
        alternatePhone: '',
        clientType: 'other',
        status: 'pending',
        priority: 'medium',
        assignedTo: '',
        notes: ''
    });

    useEffect(() => {
        fetchClients();
        fetchUsers();
    }, [filter, currentPage, searchTerm]);

    const fetchClients = async () => {
        try {
            setLoading(true);
            const params = {
                ...Object.fromEntries(Object.entries(filter).filter(([_, v]) => v !== '')),
                search: searchTerm
            };

            const response = await api.get('/clients', { params });

            if (response.data.success) {
                const allClients = response.data.clients || [];
                setAllClientsList(allClients); // Store full list for range
                setTotalClients(allClients.length);

                // Client-side pagination
                const startIndex = (currentPage - 1) * itemsPerPage;
                const endIndex = startIndex + itemsPerPage;
                setClients(allClients.slice(startIndex, endIndex));
            }
        } catch (error) {
            toast.error('Failed to fetch clients');
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const data = await userService.getAllUsers({ status: 'active' });
            setUsers(data.users || []);
        } catch (error) {
            console.error('Failed to fetch users');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await api.post('/clients', formData);
            if (response.data.success) {
                toast.success('Client added successfully!');
                setShowAddModal(false);
                resetForm();
                setCurrentPage(1);
                fetchClients();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to add client');
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);

                // Map to your Excel column names
                const clients = data.map(row => ({
                    companyName: row['Company Name'] || row.companyName || '',
                    address: row['Add.'] || row['Add'] || row.address || '',
                    city: row['City'] || row.city || '',
                    pinCode: row['Pin'] || row.pinCode || '',
                    state: row['State'] || row.state || '',
                    contactPerson: row['Contact Person'] || row.contactPerson || '',
                    phone: row['Contact No.'] || row['Contact No'] || row.phone || '',
                    email: row[' Email Id'] ||row['Email Id'] || row['Email ID'] || row.email || '',
                    clientType: 'other',
                    status: 'pending',
                    priority: 'medium'
                }));

                handleBulkImport(clients);
            } catch (error) {
                toast.error('Failed to read Excel file. Please check the format.');
                console.error(error);
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleBulkImport = async (clientsData) => {
        try {
            const response = await api.post('/clients/bulk-import', { clients: clientsData });
            if (response.data.success) {
                toast.success(`${response.data.count} clients imported successfully!`);
                setShowImportModal(false);
                setCurrentPage(1);
                fetchClients();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to import clients');
        }
    };

    const handleAssign = async (userId) => {
        if (!selectedClient) return;

        try {
            const response = await api.patch(`/clients/${selectedClient._id}/assign`, { userId });
            if (response.data.success) {
                toast.success('Client assigned successfully!');
                setShowAssignModal(false);
                setSelectedClient(null);
                fetchClients();
            }
        } catch (error) {
            toast.error('Failed to assign client');
        }
    };

    const handleBulkAssign = async (userId) => {
        if (selectedClients.length === 0) {
            toast.error('Please select clients to assign');
            return;
        }

        try {
            const promises = selectedClients.map(clientId =>
                api.patch(`/clients/${clientId}/assign`, { userId })
            );

            await Promise.all(promises);

            toast.success(`${selectedClients.length} clients assigned successfully!`);
            setShowBulkAssignModal(false);
            setSelectedClients([]);
            fetchClients();
        } catch (error) {
            toast.error('Failed to assign clients');
        }
    };

    const handleRangeAssign = async (userId) => {
        const start = parseInt(rangeStart);
        const end = parseInt(rangeEnd);

        if (!start || !end) {
            toast.error('Please enter valid range numbers');
            return;
        }

        if (start < 1 || end < 1) {
            toast.error('Range must start from 1');
            return;
        }

        if (start > end) {
            toast.error('Start number must be less than or equal to end number');
            return;
        }

        if (end > allClientsList.length) {
            toast.error(`End number cannot exceed total clients (${allClientsList.length})`);
            return;
        }

        try {
            // Get clients in range (array is 0-indexed, so subtract 1)
            const rangeClients = allClientsList.slice(start - 1, end);
            const clientIds = rangeClients.map(c => c._id);

            const promises = clientIds.map(clientId =>
                api.patch(`/clients/${clientId}/assign`, { userId })
            );

            await Promise.all(promises);

            toast.success(`Clients ${start} to ${end} (${clientIds.length} total) assigned successfully!`);
            setShowRangeAssignModal(false);
            setRangeStart('');
            setRangeEnd('');
            fetchClients();
        } catch (error) {
            toast.error('Failed to assign clients');
        }
    };

    const toggleClientSelection = (clientId) => {
        if (selectedClients.includes(clientId)) {
            setSelectedClients(selectedClients.filter(id => id !== clientId));
        } else {
            setSelectedClients([...selectedClients, clientId]);
        }
    };

    const selectAll = () => {
        if (selectedClients.length === clients.length) {
            setSelectedClients([]);
        } else {
            setSelectedClients(clients.map(c => c._id));
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this client?')) return;

        try {
            const response = await api.delete(`/clients/${id}`);
            if (response.data.success) {
                toast.success('Client deleted');
                fetchClients();
            }
        } catch (error) {
            toast.error('Failed to delete client');
        }
    };

    const resetForm = () => {
        setFormData({
            companyName: '',
            address: '',
            city: '',
            pinCode: '',
            state: '',
            contactPerson: '',
            phone: '',
            email: '',
            alternatePhone: '',
            clientType: 'other',
            status: 'pending',
            priority: 'medium',
            assignedTo: '',
            notes: ''
        });
    };

    // Pagination calculations
    const totalPages = Math.ceil(totalClients / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage + 1;
    const endIndex = Math.min(currentPage * itemsPerPage, totalClients);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            setSelectedClients([]);
        }
    };

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            }
        }

        return pages;
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold">Client Management</h1>
                    <p className="text-gray-600 mt-2">
                        Total: {totalClients} clients
                        {selectedClients.length > 0 && (
                            <span className="ml-2 text-blue-600">({selectedClients.length} selected)</span>
                        )}
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setShowRangeAssignModal(true)}
                        className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 flex items-center gap-2"
                    >
                        <FaListOl /> Assign by Range
                    </button>
                    {selectedClients.length > 0 && (
                        <button
                            onClick={() => setShowBulkAssignModal(true)}
                            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center gap-2"
                        >
                            <FaUsers /> Assign Selected ({selectedClients.length})
                        </button>
                    )}
                    <button
                        onClick={() => setShowImportModal(true)}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
                    >
                        <FaUpload /> Import Excel
                    </button>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                        <FaPlus /> Add Client
                    </button>
                </div>
            </div>

            {/* Search & Filters */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Search</label>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                            placeholder="Search company name..."
                            className="w-full px-3 py-2 border rounded-lg"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Status</label>
                        <select
                            value={filter.status}
                            onChange={(e) => {
                                setFilter({ ...filter, status: e.target.value });
                                setCurrentPage(1);
                            }}
                            className="w-full px-3 py-2 border rounded-lg"
                        >
                            <option value="">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="interested">Interested</option>
                            <option value="not_interested">Not Interested</option>
                            <option value="converted">Converted</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Client Type</label>
                        <select
                            value={filter.clientType}
                            onChange={(e) => {
                                setFilter({ ...filter, clientType: e.target.value });
                                setCurrentPage(1);
                            }}
                            className="w-full px-3 py-2 border rounded-lg"
                        >
                            <option value="">All Types</option>
                            <option value="fabricator">Fabricator</option>
                            <option value="direct_client">Direct Client</option>
                            <option value="other">Other</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Assigned To</label>
                        <select
                            value={filter.assignedTo}
                            onChange={(e) => {
                                setFilter({ ...filter, assignedTo: e.target.value });
                                setCurrentPage(1);
                            }}
                            className="w-full px-3 py-2 border rounded-lg"
                        >
                            <option value="">All Users</option>
                            {users.map(user => (
                                <option key={user._id} value={user._id}>{user.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-end">
                        <button
                            onClick={() => {
                                setFilter({ status: '', clientType: '', assignedTo: '' });
                                setSearchTerm('');
                                setCurrentPage(1);
                                setSelectedClients([]);
                            }}
                            className="w-full px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                        >
                            Clear All
                        </button>
                    </div>
                </div>
            </div>

            {/* Clients Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : clients.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        <p className="text-lg">No clients found</p>
                        <p className="text-sm mt-2">Click "Add Client" or "Import Excel" to get started</p>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left">
                                            <input
                                                type="checkbox"
                                                checked={selectedClients.length === clients.length && clients.length > 0}
                                                onChange={selectAll}
                                                className="rounded"
                                            />
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned To</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {clients.map((client, index) => {
                                        const globalIndex = (currentPage - 1) * itemsPerPage + index + 1;
                                        return (
                                            <tr key={client._id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedClients.includes(client._id)}
                                                        onChange={() => toggleClientSelection(client._id)}
                                                        className="rounded"
                                                    />
                                                </td>
                                                <td className="px-6 py-4 text-sm font-medium text-gray-900">{globalIndex}</td>
                                                <td className="px-6 py-4">
                                                    <div>
                                                        <p className="font-medium">{client.companyName}</p>
                                                        <p className="text-sm text-gray-500">{client.contactPerson}</p>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-sm">
                                                    <p>{client.phone}</p>
                                                    <p className="text-gray-500">{client.email}</p>
                                                </td>
                                                <td className="px-6 py-4 text-sm">{client.city}, {client.state}</td>
                                                <td className="px-6 py-4">
                                                    <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800 capitalize">
                                                        {client.clientType?.replace('_', ' ')}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-800 capitalize">
                                                        {client.status?.replace('_', ' ')}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm">
                                                    {client.assignedTo?.name || 'Unassigned'}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => {
                                                                setSelectedClient(client);
                                                                setShowAssignModal(true);
                                                            }}
                                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                                            title="Assign"
                                                        >
                                                            <FaUserPlus />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(client._id)}
                                                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                                                            title="Delete"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="bg-gray-50 px-6 py-4 border-t flex items-center justify-between">
                            <div className="text-sm text-gray-700">
                                Showing <span className="font-medium">{startIndex}</span> to{' '}
                                <span className="font-medium">{endIndex}</span> of{' '}
                                <span className="font-medium">{totalClients}</span> results
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <FaChevronLeft />
                                </button>

                                {getPageNumbers().map((page, index) => (
                                    <button
                                        key={index}
                                        onClick={() => typeof page === 'number' && handlePageChange(page)}
                                        className={`px-3 py-1 border rounded ${page === currentPage
                                                ? 'bg-blue-600 text-white'
                                                : page === '...'
                                                    ? 'cursor-default'
                                                    : 'hover:bg-gray-100'
                                            }`}
                                        disabled={page === '...'}
                                    >
                                        {page}
                                    </button>
                                ))}

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <FaChevronRight />
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Import Modal */}
            {showImportModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-8 max-w-md w-full">
                        <h2 className="text-2xl font-bold mb-4">Import Clients from Excel</h2>
                        <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
                            <p className="text-sm font-semibold text-blue-900 mb-2">Required Excel Columns:</p>
                            <ul className="text-xs text-blue-800 space-y-1">
                                <li>• Company Name</li>
                                <li>• Add.</li>
                                <li>• City</li>
                                <li>• Pin</li>
                                <li>• State</li>
                                <li>• Contact No.</li>
                                <li>• Email Id</li>
                            </ul>
                        </div>
                        <input
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileUpload}
                            className="w-full border rounded-lg px-3 py-2 mb-4"
                        />
                        <button
                            onClick={() => setShowImportModal(false)}
                            className="w-full bg-gray-300 py-2 rounded-lg hover:bg-gray-400"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Single Assign Modal */}
            {showAssignModal && selectedClient && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-8 max-w-md w-full">
                        <h2 className="text-2xl font-bold mb-4">Assign Client</h2>
                        <p className="mb-4">Company: <strong>{selectedClient.companyName}</strong></p>
                        <select
                            onChange={(e) => handleAssign(e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 mb-4"
                        >
                            <option value="">Select User</option>
                            {users.map(user => (
                                <option key={user._id} value={user._id}>{user.name}</option>
                            ))}
                        </select>
                        <button
                            onClick={() => {
                                setShowAssignModal(false);
                                setSelectedClient(null);
                            }}
                            className="w-full bg-gray-300 py-2 rounded-lg hover:bg-gray-400"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Bulk Assign Modal */}
            {showBulkAssignModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-8 max-w-md w-full">
                        <h2 className="text-2xl font-bold mb-4">Bulk Assign Clients</h2>
                        <p className="mb-4">
                            Assign <strong>{selectedClients.length}</strong> selected clients to:
                        </p>
                        <select
                            onChange={(e) => e.target.value && handleBulkAssign(e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 mb-4"
                        >
                            <option value="">Select User</option>
                            {users.map(user => (
                                <option key={user._id} value={user._id}>{user.name}</option>
                            ))}
                        </select>
                        <button
                            onClick={() => {
                                setShowBulkAssignModal(false);
                                setSelectedClients([]);
                            }}
                            className="w-full bg-gray-300 py-2 rounded-lg hover:bg-gray-400"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Range Assign Modal */}
            {showRangeAssignModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-8 max-w-md w-full">
                        <h2 className="text-2xl font-bold mb-4">Assign by Range</h2>
                        <p className="text-sm text-gray-600 mb-4">
                            Total clients available: <strong>{allClientsList.length}</strong>
                        </p>

                        <div className="bg-orange-50 border border-orange-200 rounded p-4 mb-4">
                            <p className="text-sm text-orange-800 mb-2">
                                💡 <strong>Tip:</strong> Use filters first to narrow down clients, then assign ranges.
                            </p>
                            <p className="text-xs text-orange-700">
                                Example: Filter "Status = Pending", then assign clients 1-50 to User A, 51-100 to User B
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">From (Client #)</label>
                                <input
                                    type="number"
                                    min="1"
                                    max={allClientsList.length}
                                    value={rangeStart}
                                    onChange={(e) => setRangeStart(e.target.value)}
                                    placeholder="1"
                                    className="w-full border rounded-lg px-3 py-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">To (Client #)</label>
                                <input
                                    type="number"
                                    min="1"
                                    max={allClientsList.length}
                                    value={rangeEnd}
                                    onChange={(e) => setRangeEnd(e.target.value)}
                                    placeholder={allClientsList.length.toString()}
                                    className="w-full border rounded-lg px-3 py-2"
                                />
                            </div>
                        </div>

                        {rangeStart && rangeEnd && parseInt(rangeEnd) >= parseInt(rangeStart) && (
                            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                                <p className="text-sm text-blue-800">
                                    Will assign <strong>{parseInt(rangeEnd) - parseInt(rangeStart) + 1}</strong> clients
                                    (#{rangeStart} to #{rangeEnd})
                                </p>
                            </div>
                        )}

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Assign To</label>
                            <select
                                id="rangeUserSelect"
                                className="w-full border rounded-lg px-3 py-2"
                            >
                                <option value="">Select User</option>
                                {users.map(user => (
                                    <option key={user._id} value={user._id}>{user.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    const userId = document.getElementById('rangeUserSelect').value;
                                    if (userId) {
                                        handleRangeAssign(userId);
                                    } else {
                                        toast.error('Please select a user');
                                    }
                                }}
                                className="flex-1 bg-orange-600 text-white py-2 rounded-lg hover:bg-orange-700"
                            >
                                Assign Range
                            </button>
                            <button
                                onClick={() => {
                                    setShowRangeAssignModal(false);
                                    setRangeStart('');
                                    setRangeEnd('');
                                }}
                                className="flex-1 bg-gray-300 py-2 rounded-lg hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminClients;